import { inject, Injectable } from '@angular/core';
import { Observable, take, tap } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { ApiHttpService } from '../../api-http.service';
import { Client, CustomHttpResponse, HydraCollectionResponse } from '../../../models';
import { withQueryString } from '../../../utils/url-utils/url-utils';

@Injectable({
  providedIn: 'root',
})
export class ClientService {
  apiService = inject(ApiHttpService<Client>);
  loading = false;
  clients: Observable<HydraCollectionResponse<Client>> = this.apiService.getMany('/clients').pipe(
    take(1),
    filter((result: CustomHttpResponse<HydraCollectionResponse<Client>>) => !!result.body),
    map((result) => result.body),
  );

  getClients(): Observable<Client[]> {
    return this.apiService.getMany('/clients').pipe(
      take(1),
      filter((result: CustomHttpResponse<HydraCollectionResponse<Client>>) => !!result.body),
      map((result) => result.body['hydra:member'] as Client[]),
    );
  }

  getAllClients(): Observable<Client[]> {
    return this.apiService.getMany('/clients').pipe(
      take(1),
      filter((result: CustomHttpResponse<HydraCollectionResponse<Client>>) => !!result.body),
      map((result) => result.body['hydra:member'] as Client[]),
    );
  }

  updateClient(iri: string, client: Client): Observable<Client> {
    return this.apiService.patch(iri, client).pipe(map((result: CustomHttpResponse<Client>) => result.body));
  }

  createClient(client: Client): Observable<Client> {
    if (!client.defaultDeliveryAddress) {
      client.defaultDeliveryAddress = null;
    }
    return this.apiService.post('/clients', client).pipe(map((result: CustomHttpResponse<Client>) => result.body));
  }

  getClientByIri(iri: string): Observable<Client> {
    return this.apiService.get(iri).pipe(
      take(1),
      map((result: CustomHttpResponse<Client>) => result.body),
    );
  }

  get(): Observable<HydraCollectionResponse<Client>> {
    return this.apiService.getMany('/clients').pipe(
      take(1),
      filter((result: CustomHttpResponse<HydraCollectionResponse<Client>>) => !!result.body),
      map((result) => result.body),
    );
  }

  generateSepa(iri: string) {
    return this.apiService.downloadFileAsBlob(`${iri}/download_sepa`);
  }

  findClients(search: string): Observable<Client[]> {
    return this.apiService.getMany(`/clients?${search}`).pipe(
      take(1),
      filter((result: CustomHttpResponse<HydraCollectionResponse<Client>>) => !!result.body),
      map((result) => result.body['hydra:member'] as Client[]),
    );
  }

  find(searchTerm?: string): Observable<HydraCollectionResponse<Client>> {
    return this.apiService.getMany(withQueryString('/clients?pagination=false', searchTerm)).pipe(
      take(1),
      filter((result: CustomHttpResponse<HydraCollectionResponse<Client>>) => !!result.body),
      map((result) => result.body),
    );
  }

  findNotMany(searchTerm?: string): Observable<HydraCollectionResponse<Client>> {
    return this.apiService.getMany(withQueryString('/clients', searchTerm)).pipe(
      take(1),
      filter((result: CustomHttpResponse<HydraCollectionResponse<Client>>) => !!result.body),
      map((result) => result.body),
    );
  }

  findClientsWithDocParserId(search?: string): Observable<Client[]> {
    const url = search
      ? `/clients?exists[docparserParserId]=true&${search}`
      : `/clients?exists[docparserParserId]=true`;
    return this.apiService.getMany(url).pipe(
      tap(() => (this.loading = true)),
      take(1),
      filter((result: CustomHttpResponse<HydraCollectionResponse<Client>>) => !!result.body),
      map((result) => result.body['hydra:member']),
      tap(() => (this.loading = false)),
    );
  }
}
